/**
 * Shared media URL utilities for worksheet question image resolution.
 * Both AiAnswerSheetUpload and WorkSheetTake previously duplicated these.
 */
import api, { getProxyBaseUrl } from "../api";

/**
 * Resolve a raw URL (possibly an R2 CDN URL) to an API proxy path.
 * Converts pub-*.r2.dev URLs to /proxy/visual/{key} to avoid CORS issues.
 */
export function makeAbsoluteUrl(raw) {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (trimmed.startsWith("data:")) return trimmed;
  // A signed link (students' explanation videos) only works exactly as issued; never proxy it.
  if (/[?&]X-Amz-Signature=/i.test(trimmed)) return trimmed;
  // Videos play straight from R2; the image proxy refuses them.
  if (/\.(?:mp4|webm|mov|m4v)(?:[?#]|$)/i.test(trimmed)) return trimmed;
  const r2Match = trimmed.match(/^https?:\/\/pub-[^/]+\.r2\.dev\/(.+)$/) ||
    trimmed.match(/^https?:\/\/[^/]+\.r2\.cloudflarestorage\.com\/(.+)$/);
  if (r2Match) {
    const baseURL = (getProxyBaseUrl ? getProxyBaseUrl() : null) || api.defaults?.baseURL || "";
    return `${baseURL.replace(/\/$/, "")}/proxy/visual/${r2Match[1]}`;
  }
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  const baseURL = (getProxyBaseUrl ? getProxyBaseUrl() : null) || api.defaults?.baseURL || "";
  return baseURL ? `${baseURL.replace(/\/$/, "")}/${trimmed.replace(/^\//, "")}` : trimmed;
}

/**
 * Resolve an answer option's figure. Options carry their picture on `image_url`, but a figure
 * stored inline (no R2 upload available) lives on `visual.svg` — without this fallback a picture
 * answer silently renders as a blank box, which is the one failure a child cannot work around.
 */
export function resolveOptionImageUrl(option) {
  if (!option) return null;
  const resolved = makeAbsoluteUrl(option.image_url || option.visual_image_url || option.visual?.url || null);
  if (resolved) return resolved;
  const svg = typeof option.visual?.svg === "string" ? option.visual.svg : option.svg;
  if (typeof svg === "string" && svg.trim().startsWith("<svg")) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }
  return null;
}

/**
 * Extract the best image URL from a question object, checking all known field locations.
 */
export function resolveQuestionImageUrl(question) {
  if (!question) return null;
  const raw =
    question.visual_image_url ||
    question.image_url ||
    question.visual_url ||
    (typeof question.visual === "string" ? question.visual : null) ||
    question.visual?.url ||
    question.visual?.r2_url ||
    question.visual?.public_url ||
    question.visual?.image_url ||
    null;
  const resolved = makeAbsoluteUrl(raw);
  if (resolved) return resolved;
  // Inline SVG fallback (visual type svg-inline, stored when R2 upload is unavailable)
  const svg = question.visual?.svg;
  if (typeof svg === "string" && svg.trim().startsWith("<svg")) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }
  return null;
}
